import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocationState } from '@/contexts/LocationContext'
import { useLanguage } from '@/lib/lang'
import { t } from '@/lib/i18n'
import { useLocation, useNavigate } from 'react-router-dom'
import { Building2, Layers, Users, ChevronRight, Loader2, AlertCircle, RefreshCw, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  getPanchayatDataProvider,
  type PanchayatBlockRecord,
  type PanchayatDistrictRecord,
  type PanchayatGramPanchayatRecord,
} from '@/lib/panchayat-data'

type PanchayatSelectorMode = 'navigate' | 'persistent'

type LockedGp = {
  district: string
  block: string
  village: string
}

type PanchayatSelection = {
  level: 'gp' | 'bs' | 'zp'
  district: string
  block: string
  village: string
}

type PanchayatSelectorProps = {
  mode?: PanchayatSelectorMode
  lockedGp?: LockedGp | null
  storageKey?: string
  embedded?: boolean
  hideLevelTabs?: boolean
  showSearch?: boolean
  showChips?: boolean
  disablePersistence?: boolean
  selection?: Pick<PanchayatSelection, 'district' | 'block' | 'village'>
  onSelectionChange?: (next: PanchayatSelection) => void
}

type ProgressiveSearchEntry = {
  district: { en: string; hi: string; pa: string }
  block: { en: string; hi: string; pa: string }
  gp: { en: string; hi: string; pa: string }
  gpId: string
}

function normalizeName(value: string) {
  return String(value || '').trim().toLowerCase()
}

function matchesEnglishName(value: { names?: { en?: string } } | undefined, target: string) {
  return normalizeName(value?.names?.en || '') === normalizeName(target)
}

function toNames(value?: { en?: string; hi?: string; pa?: string }) {
  const en = String(value?.en || '').trim()
  return {
    en,
    hi: String(value?.hi || en).trim(),
    pa: String(value?.pa || en).trim(),
  }
}

function formatSearchEntry(entry: ProgressiveSearchEntry, lang: string) {
  const district = lang === 'en' ? entry.district.en : (entry.district[lang as 'en' | 'hi' | 'pa'] || entry.district.en)
  const block = lang === 'en' ? entry.block.en : (entry.block[lang as 'en' | 'hi' | 'pa'] || entry.block.en)
  const gp = lang === 'en' ? entry.gp.en : (entry.gp[lang as 'en' | 'hi' | 'pa'] || entry.gp.en)
  return [gp, block, district].filter(Boolean).join(' • ')
}

function buildSearchEntries(args: {
  districts: PanchayatDistrictRecord[]
  blocks: PanchayatBlockRecord[]
  villages: PanchayatGramPanchayatRecord[]
  currentDistrict: string
  currentBlock: string
}) {
  if (!args.currentDistrict || !args.currentBlock) return []
  const district = args.districts.find((entry) => matchesEnglishName(entry, args.currentDistrict))
  const block = args.blocks.find((entry) => matchesEnglishName(entry, args.currentBlock))
  if (!district || !block) return []
  return args.villages.map<ProgressiveSearchEntry>((gp) => ({
    district: toNames(district.names),
    block: toNames(block.names),
    gp: toNames(gp.names),
    gpId: gp.id,
  }))
}

function filterSearchEntries(entries: ProgressiveSearchEntry[], query: string, limit: number) {
  const normalizedQuery = normalizeName(query)
  if (!normalizedQuery) return []
  const scored = entries
    .map((entry) => {
      const gpName = normalizeName(entry.gp.en)
      const blockName = normalizeName(entry.block.en)
      const districtName = normalizeName(entry.district.en)
      let score = 0
      if (gpName.startsWith(normalizedQuery)) score += 100
      else if (gpName.includes(normalizedQuery)) score += 70
      if (blockName.includes(normalizedQuery)) score += 20
      if (districtName.includes(normalizedQuery)) score += 10
      return { entry, score }
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.entry.gp.en.localeCompare(b.entry.gp.en))

  return scored.slice(0, limit).map((entry) => entry.entry)
}

export const PanchayatSelector = ({
  mode = 'navigate',
  lockedGp = null,
  storageKey = 'home_gp_selection',
  embedded = false,
  hideLevelTabs,
  showSearch: showSearchProp,
  showChips: showChipsProp,
  disablePersistence = false,
  selection,
  onSelectionChange
}: PanchayatSelectorProps) => {
  const {
    selectedDistrict, setSelectedDistrict,
    selectedBlock, setSelectedBlock,
    selectedVillage, setSelectedVillage,
  } = useLocationState()

  const { lang } = useLanguage()
  const location = useLocation()
  const navigate = useNavigate()
  const provider = useMemo(() => getPanchayatDataProvider(), [])

  const isControlled = Boolean(selection && typeof onSelectionChange === 'function')
  const resolvedHideLevelTabs = typeof hideLevelTabs === 'boolean' ? hideLevelTabs : mode === 'persistent'
  const showSearch = showSearchProp !== false
  const showChips = showChipsProp !== false
  const storage = useMemo(() => {
    if (disablePersistence) return null
    if (typeof window === 'undefined') return null
    if (storageKey.startsWith('institution_')) return sessionStorage
    return localStorage
  }, [disablePersistence, storageKey])

  const [activeLevel, setActiveLevel] = useState<'gp' | 'bs' | 'zp'>(() => {
    return mode === 'persistent' ? 'gp' : 'gp'
  })

  const isLocked = !!(lockedGp?.district && lockedGp?.block && lockedGp?.village)

  const currentDistrict = isLocked ? (lockedGp?.district || '') : (isControlled ? (selection?.district || '') : selectedDistrict)
  const currentBlock = isLocked ? (lockedGp?.block || '') : (isControlled ? (selection?.block || '') : selectedBlock)
  const currentVillage = isLocked ? (lockedGp?.village || '') : (isControlled ? (selection?.village || '') : selectedVillage)

  const [districts, setDistricts] = useState<PanchayatDistrictRecord[]>([])
  const [blocks, setBlocks] = useState<PanchayatBlockRecord[]>([])
  const [villages, setVillages] = useState<PanchayatGramPanchayatRecord[]>([])
  const [loadingDistricts, setLoadingDistricts] = useState(false)
  const [loadingBlocks, setLoadingBlocks] = useState(false)
  const [loadingVillages, setLoadingVillages] = useState(false)
  const [selectorError, setSelectorError] = useState<Error | null>(null)
  const blocksCacheRef = useRef(new Map<string, PanchayatBlockRecord[]>())
  const villagesCacheRef = useRef(new Map<string, PanchayatGramPanchayatRecord[]>())
  const chipEntriesRef = useRef(new Map<string, ProgressiveSearchEntry>())

  const currentDistrictRecord = useMemo(() => {
    return districts.find((district) => matchesEnglishName(district, currentDistrict)) || null
  }, [currentDistrict, districts])

  const currentBlockRecord = useMemo(() => {
    return blocks.find((block) => matchesEnglishName(block, currentBlock)) || null
  }, [blocks, currentBlock])

  const effectiveSearchEntries = useMemo(() => buildSearchEntries({
    districts,
    blocks,
    villages,
    currentDistrict,
    currentBlock,
  }), [blocks, currentBlock, currentDistrict, districts, villages])

  const entryById = useMemo(() => {
    const map = new Map<string, ProgressiveSearchEntry>()
    for (const entry of effectiveSearchEntries) map.set(entry.gpId, entry)
    for (const [id, entry] of chipEntriesRef.current.entries()) {
      if (!map.has(id)) map.set(id, entry)
    }
    return map
  }, [effectiveSearchEntries])

  const loadDistricts = useCallback(async () => {
    setLoadingDistricts(true)
    setSelectorError(null)
    try {
      const next = await provider.getDistricts()
      setDistricts(next)
    } catch (error) {
      setSelectorError(error instanceof Error ? error : new Error(String(error)))
    } finally {
      setLoadingDistricts(false)
    }
  }, [provider])

  const loadBlocks = useCallback(async (districtName: string) => {
    const key = normalizeName(districtName)
    if (!key) {
      setBlocks([])
      return
    }
    const cached = blocksCacheRef.current.get(key)
    if (cached) {
      setBlocks(cached)
      return
    }
    setLoadingBlocks(true)
    setSelectorError(null)
    try {
      const next = await provider.getBlocks(districtName)
      blocksCacheRef.current.set(key, next)
      setBlocks(next)
    } catch (error) {
      setSelectorError(error instanceof Error ? error : new Error(String(error)))
    } finally {
      setLoadingBlocks(false)
    }
  }, [provider])

  const loadVillages = useCallback(async (blockName: string, districtName: string) => {
    const key = [normalizeName(districtName), normalizeName(blockName)].filter(Boolean).join("|")
    if (!key) {
      setVillages([])
      return
    }
    const cached = villagesCacheRef.current.get(key)
    if (cached) {
      setVillages(cached)
      return
    }
    setLoadingVillages(true)
    setSelectorError(null)
    try {
      const next = await provider.getGramPanchayats(blockName, districtName)
      villagesCacheRef.current.set(key, next)
      setVillages(next)
    } catch (error) {
      setSelectorError(error instanceof Error ? error : new Error(String(error)))
    } finally {
      setLoadingVillages(false)
    }
  }, [provider])

  const [resolvedVillageNameEn, setResolvedVillageNameEn] = useState<string>("")
  useEffect(() => {
    if (activeLevel !== "gp") {
      setResolvedVillageNameEn("")
      return
    }
    if (!currentDistrict || !currentBlock || !currentVillage) {
      setResolvedVillageNameEn("")
      return
    }

    const entry = entryById.get(String(currentVillage))
    if (entry?.gp?.en) {
      setResolvedVillageNameEn(entry.gp.en)
      return
    }

    let cancelled = false
    ;(async () => {
      try {
        const districtId = currentDistrictRecord?.id || currentDistrict
        const blockId = currentBlockRecord?.id || currentBlock
        const institution = await provider.getInstitution(String(currentVillage), districtId, blockId)
        if (cancelled) return
        setResolvedVillageNameEn(String(institution?.names?.en || currentVillage))
      } catch {
        if (cancelled) return
        setResolvedVillageNameEn(String(currentVillage))
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    activeLevel,
    currentBlock,
    currentBlockRecord?.id,
    currentDistrict,
    currentDistrictRecord?.id,
    currentVillage,
    entryById,
    provider,
  ])

  useEffect(() => {
    void loadDistricts()
  }, [loadDistricts])

  useEffect(() => {
    if (!currentDistrict) {
      setBlocks([])
      setVillages([])
      return
    }
    void loadBlocks(currentDistrict)
  }, [currentDistrict, loadBlocks])

  useEffect(() => {
    if (!currentBlock || !currentDistrict) {
      setVillages([])
      return
    }
    void loadVillages(currentBlock, currentDistrict)
  }, [currentBlock, currentDistrict, loadVillages])

  const effectiveDistricts = useMemo(() => {
    if (!isLocked) return districts
    const d = lockedGp?.district || ''
    const match = districts.find(x => x.names?.en === d)
    return match ? [match] : []
  }, [districts, isLocked, lockedGp?.district])

  const effectiveBlocks = useMemo(() => {
    if (!isLocked) return blocks
    const b = lockedGp?.block || ''
    const match = blocks.find(x => x.names?.en === b)
    return match ? [match] : []
  }, [blocks, isLocked, lockedGp?.block])

  const effectiveVillages = useMemo(() => {
    if (!isLocked) return villages
    const v = lockedGp?.village || ''
    const match = villages.find(x => x.names?.en === v || x.id === v)
    return match ? [match] : []
  }, [villages, isLocked, lockedGp?.village])

  const loading = loadingDistricts
  const error = selectorError
  const retry = useCallback(() => {
    if (!currentDistrict) {
      void loadDistricts()
      return
    }
    if (!currentBlock) {
      void loadBlocks(currentDistrict)
      return
    }
    void loadVillages(currentBlock, currentDistrict)
  }, [currentBlock, currentDistrict, loadBlocks, loadDistricts, loadVillages])

  const initRef = useRef<string>("")
  useEffect(() => {
    if (disablePersistence) return
    if (mode !== 'persistent') return

    const sig = [
      storageKey,
      isLocked ? 'locked' : 'unlocked',
      lockedGp?.district || '',
      lockedGp?.block || '',
      lockedGp?.village || '',
      isControlled ? 'controlled' : 'uncontrolled'
    ].join('|')

    if (initRef.current === sig) return
    initRef.current = sig

    if (isLocked && lockedGp) {
      const next = { level: activeLevel, district: lockedGp.district, block: lockedGp.block, village: lockedGp.village }
      if (isControlled) {
        if (
          next.district !== currentDistrict ||
          next.block !== currentBlock ||
          next.village !== currentVillage
        ) {
          onSelectionChange?.(next)
        }
      } else {
        if (selectedDistrict !== lockedGp.district) setSelectedDistrict(lockedGp.district)
        if (selectedBlock !== lockedGp.block) setSelectedBlock(lockedGp.block)
        if (selectedVillage !== lockedGp.village) setSelectedVillage(lockedGp.village)
      }
      return
    }

    try {
      const raw = storage?.getItem(storageKey)
      if (!raw) return
      const parsed = JSON.parse(raw) as { district?: string; block?: string; village?: string; level?: 'gp' | 'bs' | 'zp' }
      const lvl = parsed.level || 'gp'
      if (lvl === 'gp' || lvl === 'bs' || lvl === 'zp') {
        setActiveLevel((prev) => (prev === lvl ? prev : lvl))
      }
      const nextDistrict = typeof parsed.district === 'string' ? parsed.district : ''
      const nextBlock = typeof parsed.block === 'string' ? parsed.block : ''
      const nextVillage = typeof parsed.village === 'string' ? parsed.village : ''

      if (isControlled) {
        if (
          nextDistrict !== currentDistrict ||
          nextBlock !== currentBlock ||
          nextVillage !== currentVillage
        ) {
          onSelectionChange?.({ level: lvl, district: nextDistrict, block: nextBlock, village: nextVillage })
        }
      } else {
        if (typeof parsed.district === 'string' && parsed.district !== selectedDistrict) setSelectedDistrict(parsed.district)
        if (typeof parsed.block === 'string' && parsed.block !== selectedBlock) setSelectedBlock(parsed.block)
        if (typeof parsed.village === 'string' && parsed.village !== selectedVillage) setSelectedVillage(parsed.village)
      }
    } catch {
      void 0
    }
  }, [
    disablePersistence,
    mode,
    storageKey,
    storage,
    isLocked,
    lockedGp?.district,
    lockedGp?.block,
    lockedGp?.village,
    isControlled,
    onSelectionChange,
    activeLevel,
    currentDistrict,
    currentBlock,
    currentVillage,
    selectedDistrict,
    selectedBlock,
    selectedVillage,
    setSelectedDistrict,
    setSelectedBlock,
    setSelectedVillage,
  ])

  useEffect(() => {
    if (disablePersistence) return
    if (mode !== 'persistent') return
    if (isLocked) return
    try {
      storage?.setItem(storageKey, JSON.stringify({
        level: activeLevel,
        district: currentDistrict,
        block: currentBlock,
        village: currentVillage,
      }))
    } catch {
      void 0
    }
  }, [disablePersistence, mode, isLocked, storageKey, activeLevel, currentDistrict, currentBlock, currentVillage, storage])

  // Navigation Logic: Triggered when the required selection for the active level is complete
  const skipInitialAutoNavigateOnCleanHomeRef = useRef(true)
  useEffect(() => {
    if (mode !== 'navigate') return
    if (skipInitialAutoNavigateOnCleanHomeRef.current) {
      skipInitialAutoNavigateOnCleanHomeRef.current = false
      if (location.pathname === "/" && !location.search) return
    }
    // Only navigate if we have a valid selection for the current level
    let shouldNavigate = false
    const search = new URLSearchParams()

    if (activeLevel === 'zp' && currentDistrict) {
      shouldNavigate = true
      search.set('level', 'zp')
      search.set('district', currentDistrict)
    } else if (activeLevel === 'bs' && currentDistrict && currentBlock) {
      shouldNavigate = true
      search.set('level', 'bs')
      search.set('district', currentDistrict)
      search.set('block', currentBlock)
    } else if (activeLevel === 'gp' && currentDistrict && currentBlock && currentVillage) {
      if (!resolvedVillageNameEn) return
      shouldNavigate = true
      search.set('level', 'gp')
      search.set('district', currentDistrict)
      search.set('block', currentBlock)
      search.set('village', resolvedVillageNameEn)
    }

    if (shouldNavigate) {
      const prefix = lang === 'en' ? '' : `/${lang}`
      // Use replace to avoid building up history of intermediate selections
      navigate(`${prefix}/gp?${search.toString()}`, { replace: true })
    }
  }, [mode, activeLevel, currentDistrict, currentBlock, currentVillage, lang, navigate, resolvedVillageNameEn, location.pathname, location.search])

  const chipsKey = `${storageKey}_chips`
  const [selectedGpIds, setSelectedGpIds] = useState<string[]>(() => {
    if (!showChips || disablePersistence) return []
    try {
      const raw = storage?.getItem(chipsKey)
      const parsed = raw ? JSON.parse(raw) : null
      if (Array.isArray(parsed)) return parsed.filter((x) => typeof x === 'string')
    } catch {
      void 0
    }
    return []
  })

  useEffect(() => {
    if (!showChips || disablePersistence) return
    if (isLocked) return
    try {
      storage?.setItem(chipsKey, JSON.stringify(selectedGpIds))
    } catch {
      void 0
    }
  }, [chipsKey, disablePersistence, isLocked, selectedGpIds, showChips, storage])

  useEffect(() => {
    if (!showChips) return
    if (!currentVillage) return
    const id = String(currentVillage)
    setSelectedGpIds((prev) => (prev.includes(id) ? prev : [id, ...prev].slice(0, 25)))
  }, [currentVillage, showChips])

  const removeChip = (id: string) => {
    setSelectedGpIds((prev) => prev.filter((x) => x !== id))
  }

  const clearChips = () => {
    setSelectedGpIds([])
  }

  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [suggestions, setSuggestions] = useState<ProgressiveSearchEntry[]>([])
  const suggestCacheRef = useRef(new Map<string, ProgressiveSearchEntry[]>())
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!showSearch) return
    const id = window.setTimeout(() => setDebouncedQuery(query), 180)
    return () => window.clearTimeout(id)
  }, [query, showSearch])

  useEffect(() => {
    if (!showSearch) return
    const q = String(debouncedQuery || '').trim()
    if (!q) {
      setSuggestions([])
      setActiveIndex(-1)
      setIsOpen(false)
      return
    }

    const cacheKey = [lang, currentDistrict || '', currentBlock || '', q].join('|')
    const cached = suggestCacheRef.current.get(cacheKey)
    if (cached) {
      setSuggestions(cached)
      setActiveIndex(cached.length ? 0 : -1)
      setIsOpen(true)
      return
    }

    const next = filterSearchEntries(effectiveSearchEntries, q, 10)

    suggestCacheRef.current.set(cacheKey, next)
    setSuggestions(next)
    setActiveIndex(next.length ? 0 : -1)
    setIsOpen(true)
  }, [debouncedQuery, currentBlock, currentDistrict, effectiveSearchEntries, lang, showSearch])

  useEffect(() => {
    if (!showSearch) return
    const onDown = (e: MouseEvent) => {
      const root = rootRef.current
      if (!root) return
      if (root.contains(e.target as any)) return
      setIsOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [showSearch])

  const selectEntry = (e: ProgressiveSearchEntry) => {
    chipEntriesRef.current.set(e.gpId, e)
    if (isControlled) {
      onSelectionChange?.({ level: 'gp', district: e.district.en, block: e.block.en, village: e.gpId })
    } else {
      setSelectedDistrict(e.district.en)
      setSelectedBlock(e.block.en)
      setSelectedVillage(e.gpId)
    }
    setQuery('')
    setDebouncedQuery('')
    setIsOpen(false)
    setActiveIndex(-1)
    if (showChips) setSelectedGpIds((prev) => (prev.includes(e.gpId) ? prev : [e.gpId, ...prev].slice(0, 25)))
  }

  const listboxId = 'gp-suggest-listbox'

  // Helper to render Tab Button
  const TabButton = ({ level, label, icon: Icon }: { level: 'zp' | 'bs' | 'gp', label: string, icon: React.ElementType }) => {
    const line1 = t(`level_${level}_short_line1`, lang) || label
    const line2 = t(`level_${level}_short_line2`, lang) || ""

    return (
      <button
        onClick={() => {
          if (resolvedHideLevelTabs) return
          setActiveLevel(level)
          // Reset lower-level selections when switching levels to avoid stale state
          if (level === 'zp') {
            if (isControlled) onSelectionChange?.({ level: 'zp', district: currentDistrict, block: '', village: '' })
            else {
              setSelectedBlock('')
              setSelectedVillage('')
            }
          } else if (level === 'bs') {
            if (isControlled) onSelectionChange?.({ level: 'bs', district: currentDistrict, block: currentBlock, village: '' })
            else setSelectedVillage('')
          }
        }}
        className={`
          relative flex flex-1 sm:flex-none min-w-[90px] items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-2 sm:py-3 rounded-t-lg transition-all duration-200 border-b-2
          ${activeLevel === level
            ? 'border-primary text-primary bg-primary/5 font-bold shadow-sm'
            : 'border-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground font-medium'}
        `}
        role="tab"
        aria-selected={activeLevel === level}
        disabled={resolvedHideLevelTabs}
      >
        <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${activeLevel === level ? 'text-primary' : 'opacity-70'}`} />
        <div className="flex flex-col items-center leading-tight sm:leading-normal">
          <span className="text-[11px] sm:text-base whitespace-nowrap uppercase tracking-tight sm:tracking-normal sm:normal-case">{line1}</span>
          {line2 && (
            <span className="text-[11px] sm:text-base whitespace-nowrap uppercase tracking-tight sm:tracking-normal sm:normal-case sm:hidden">{line2}</span>
          )}
          <span className="hidden sm:inline text-sm sm:text-base whitespace-nowrap ml-1">{line2}</span>
        </div>
        {activeLevel === level && (
          <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
        )}
      </button>
    )
  }

  return (
    <div className={embedded ? "w-full" : "bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-b border-border/40 py-6"}>
      <div className={embedded ? "w-full" : "container mx-auto px-4"}>

        {/* Error State */}
        {error && (
          <div className="max-w-4xl mx-auto mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 flex items-center justify-between gap-4 animate-in fade-in slide-in-from-top-2">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 text-red-600" />
              <div>
                <p className="font-semibold text-sm">{t('admin_data_load_failed', lang)}</p>
                <p className="text-xs opacity-90">{error.message}</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={retry}
              className="border-red-200 hover:bg-red-100 hover:text-red-800 text-red-700 h-8"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-2" />
              {t('retry', lang)}
            </Button>
          </div>
        )}

        {/* Level Tabs - Centered Alignment */}
        {!resolvedHideLevelTabs && (
          <div className="relative mb-6">
            <div
              className="flex justify-between sm:justify-center gap-1 sm:gap-2 border-b border-border/50 px-1 pb-1"
              role="tablist"
            >
              <TabButton level="zp" label={t('level_zp', lang)} icon={Building2} />
              <TabButton level="bs" label={t('level_bs', lang) || t('block_samitis', lang)} icon={Layers} />
              <TabButton level="gp" label={t('level_gp', lang)} icon={Users} />
            </div>
          </div>
        )}

        <div className="max-w-4xl mx-auto" ref={rootRef}>

          {/* Dropdowns Grid - Labels Removed */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-in fade-in slide-in-from-top-2 duration-300 max-w-4xl mx-auto mt-4">
            {/* District Select - Always Visible */}
            <div className={`space-y-1.5 ${activeLevel === 'zp' ? 'sm:col-start-2 sm:col-span-1' : ''}`}>
              <div className="relative">
                <select
                  className="input-select appearance-none pr-10 shadow-sm hover:border-primary/50 focus:border-primary transition-colors disabled:opacity-70 disabled:bg-muted/50"
                  value={currentDistrict}
                  onChange={(e) => {
                    if (isLocked) return
                    const next = e.target.value
                    if (isControlled) onSelectionChange?.({ level: activeLevel, district: next, block: '', village: '' })
                    else setSelectedDistrict(next)
                  }}
                  disabled={loadingDistricts || !!error}
                  aria-label={t('select_district', lang)}
                >
                  <option value="">{t('select_district', lang)}</option>
                  {loading && !effectiveDistricts.length ? (
                    <option disabled>{t('loading_districts', lang)}</option>
                  ) : effectiveDistricts.length === 0 ? (
                    <option disabled>{t('no_districts_available', lang)}</option>
                  ) : (
                    effectiveDistricts.map((d) => (
                      <option key={d.id} value={d.names.en}>
                        {lang === 'en' ? d.names.en : (d.names[lang] || d.names.en)}
                      </option>
                    ))
                  )}
                </select>
                {loadingDistricts ? (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
                ) : (
                  <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none rotate-90" />
                )}
              </div>
            </div>

            {/* Block Select - Visible for BS and GP */}
            {(activeLevel === 'bs' || activeLevel === 'gp') && (
              <div className={`space-y-1.5 ${activeLevel === 'bs' ? 'sm:col-span-1' : ''}`}>
                <div className="relative">
                  <select
                    className="input-select appearance-none pr-10 shadow-sm hover:border-primary/50 focus:border-primary transition-colors disabled:opacity-50 disabled:bg-muted/50"
                    value={currentBlock}
                    onChange={(e) => {
                      if (isLocked) return
                      const next = e.target.value
                      if (isControlled) onSelectionChange?.({ level: activeLevel, district: currentDistrict, block: next, village: '' })
                      else setSelectedBlock(next)
                    }}
                    disabled={!currentDistrict || loadingBlocks}
                    aria-label={t('select_block', lang)}
                  >
                    <option value="">{t('select_block', lang)}</option>
                    {effectiveBlocks.map((b) => (
                      <option key={b.id} value={b.names.en}>
                        {lang === 'en' ? b.names.en : (b.names[lang] || b.names.en)}
                      </option>
                    ))}
                  </select>
                  <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none rotate-90" />
                </div>
              </div>
            )}

            {/* Village Select - Visible for GP only */}
            {activeLevel === 'gp' && (
              <div className="space-y-1.5">
                <div className="relative">
                  <select
                    className="input-select appearance-none pr-10 shadow-sm hover:border-primary/50 focus:border-primary transition-colors disabled:opacity-50 disabled:bg-muted/50"
                    value={currentVillage}
                    onChange={(e) => {
                      if (isLocked) return
                      const next = e.target.value
                      if (isControlled) onSelectionChange?.({ level: activeLevel, district: currentDistrict, block: currentBlock, village: next })
                      else setSelectedVillage(next)
                    }}
                    disabled={!currentBlock || loadingVillages}
                    aria-label={t('select_village', lang)}
                  >
                    <option value="">{t('select_village', lang)}</option>
                    {effectiveVillages.map((v) => (
                      <option key={v.id} value={v.id}>
                        {lang === 'en' ? v.names.en : (v.names[lang] || v.names.en)}
                      </option>
                    ))}
                  </select>
                  <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none rotate-90" />
                </div>
              </div>
            )}
          </div>

          {showSearch ? (
            <div className="mt-4">
              <div className="relative">
                <label className="sr-only" htmlFor="gp-search">
                  {t('search_gp_label', lang)}
                </label>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="gp-search"
                  value={query}
                  onChange={(ev) => {
                    setQuery(ev.target.value)
                    setIsOpen(true)
                  }}
                  onFocus={() => {
                    if (suggestions.length) setIsOpen(true)
                  }}
                  onKeyDown={(ev) => {
                    if (!isOpen && (ev.key === 'ArrowDown' || ev.key === 'ArrowUp')) {
                      setIsOpen(true)
                      return
                    }
                    if (ev.key === 'Escape') {
                      setIsOpen(false)
                      return
                    }
                    if (ev.key === 'ArrowDown') {
                      ev.preventDefault()
                      setActiveIndex((i) => {
                        const next = Math.min((suggestions.length || 1) - 1, i + 1)
                        return next
                      })
                      return
                    }
                    if (ev.key === 'ArrowUp') {
                      ev.preventDefault()
                      setActiveIndex((i) => Math.max(0, i - 1))
                      return
                    }
                    if (ev.key === 'Enter') {
                      if (!isOpen) return
                      ev.preventDefault()
                      const picked = suggestions[activeIndex] || suggestions[0]
                      if (picked) selectEntry(picked)
                      return
                    }
                  }}
                  placeholder={t('search_gp_placeholder', lang)}
                  className="pl-9 pr-10"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={isOpen}
                  aria-controls={listboxId}
                  aria-activedescendant={activeIndex >= 0 ? `gp-suggest-${activeIndex}` : undefined}
                />
                {query.trim().length > 0 && (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center rounded hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    aria-label={t('clear_search', lang)}
                    onClick={() => {
                      setQuery('')
                      setDebouncedQuery('')
                      setIsOpen(false)
                      setActiveIndex(-1)
                    }}
                  >
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                )}
              </div>

              {isOpen && query.trim().length > 0 && (
                <div
                  id={listboxId}
                  role="listbox"
                  className="mt-2 w-full rounded-md border bg-white shadow-sm max-h-72 overflow-auto"
                >
                  {suggestions.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      {t('search_gp_no_matches', lang)}
                    </div>
                  ) : (
                    suggestions.map((s, idx) => (
                      <button
                        key={`${s.gpId}_${idx}`}
                        id={`gp-suggest-${idx}`}
                        role="option"
                        aria-selected={idx === activeIndex}
                        type="button"
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-muted focus-visible:outline-none focus-visible:bg-muted ${idx === activeIndex ? 'bg-muted' : ''}`}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => selectEntry(s)}
                      >
                        {formatSearchEntry(s, lang)}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          ) : null}

          {showChips && selectedGpIds.length > 0 && (
            <div className="max-w-4xl mx-auto mt-4 flex flex-wrap items-center gap-2">
              <div className="text-xs text-muted-foreground mr-1">{t('selected_panchayats', lang)}</div>
              {selectedGpIds
                .map((id) => entryById.get(id))
                .filter(Boolean)
                .map((e) => {
                  const label = formatSearchEntry(e as ProgressiveSearchEntry, lang)
                  return (
                    <span key={(e as ProgressiveSearchEntry).gpId} className="inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1 text-sm">
                      <button
                        type="button"
                        className="text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                        onClick={() => selectEntry(e as ProgressiveSearchEntry)}
                        aria-label={t('reselect_panchayat', lang)}
                      >
                        {label}
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-full h-6 w-6 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        aria-label={t('remove_selection', lang)}
                        onClick={() => removeChip((e as ProgressiveSearchEntry).gpId)}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  )
                })}
              <button
                type="button"
                className="text-xs text-blue-600 hover:underline ml-1"
                onClick={clearChips}
              >
                {t('clear_all', lang)}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}
